import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ApprovalModalV2 from './ApprovalModalV2'
import ApprovalModalV3 from './ApprovalModalV3'

// Feature flag for V3 modal - set to true to enable new modular architecture
// V3 benefits: Better code organization, easier testing, improved maintainability
// Rollback: Set to false to use V2
const USE_APPROVAL_MODAL_V3 = true
import VideoApprovalCard from './VideoApprovalCard'
import ProductProposalCard from './ProductProposalCard'
import { ErrorBoundary } from '../shared/ErrorBoundary'
import { api } from '../../services/api'
import { FALLBACK_IMAGE, applyFallbackIfNeeded } from '../../utils/imageFallback'
import { buildBlobUrl } from '../../utils/blobUrl'
import { useWebSocket } from '../../contexts/WebSocketContext'
import { useAccount } from '../../hooks/useAccount'
import { usePendingCount } from '../../hooks/usePendingCount'
import { trackStoryApproved, trackCaptionRegeneration, trackStoryScheduled } from '../../utils/analytics'

const resolveCaptionId = (item) =>
  item?.current_caption_id ||
  item?.caption_row_key ||
  item?.caption?.row_key ||
  item?.caption?.rowKey ||
  item?.caption?.table_row_key ||
  item?.caption?.tableRowKey ||
  item?.caption_id ||
  item?.caption?.caption_id ||
  item?.caption?.id ||
  item?.captionId ||
  item?.caption_uuid ||
  item?.current_caption?.caption_id ||
  item?.current_caption?.id ||
  null

const resolveSentiment = (item) => {
  if (!item) {
    return null
  }

  return (
    item.sentiment ||
    item.sentiment_category ||
    item.sentimentCategory ||
    item.sentiment_tag ||
    (typeof item.partition_key === 'string'
      ? item.partition_key.split('#')[0]
      : null) ||
    (typeof item.partitionKey === 'string'
      ? item.partitionKey.split('#')[0]
      : null) ||
    null
  )
}

function ApprovalPage() {
  const { t } = useTranslation('approval')
  const { selectedAccountId, isLoadingAccounts } = useAccount()
  const { refresh: refreshPendingCount } = usePendingCount()
  const [pendingItems, setPendingItems] = useState([])
  const [processingCount, setProcessingCount] = useState({ pending: 0, analyzed: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [loadingProcessing, setLoadingProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [processingAction, setProcessingAction] = useState(false)
  const [actionMessage, setActionMessage] = useState(null)
  const [approvingItems, setApprovingItems] = useState(new Set()) // Track items being approved
  const regenerationWatchers = useRef(new Map())
  const isMounted = useRef(true)
  const { subscribe, isConnected } = useWebSocket()

  const enrichWithCaptions = useCallback(async (items) => {
    return Promise.all(
      items.map(async (item) => {
        try {
          const params = { image_id: item.image_id, limit: 5 }
          if (selectedAccountId) {
            params.instagram_account_id = selectedAccountId
          }
          const data = await api.getCaptions(params)
          const captions = data?.captions ?? []
          const byCurrentId = captions.find(
            (caption) => caption.caption_id === item.current_caption_id
          )
          const activeCaption =
            byCurrentId || captions.find((caption) => caption.is_active) || captions[0]

          const existingRowKey =
            item.caption_id ||
            item.current_caption_id ||
            item.caption_row_key ||
            item.caption?.row_key ||
            item.caption?.table_row_key

          const baseItem = {
            ...item,
            caption_row_key: existingRowKey || null,
          }

          const mergeFeedback = (source) => {
            const rejectionReason =
              source?.rejection_reason ?? baseItem.last_regeneration_dislike ?? item.last_regeneration_dislike ?? null
            const regenerationWishes =
              source?.regeneration_wishes ?? baseItem.last_regeneration_wish ?? item.last_regeneration_wish ?? null
            const combinedFeedback = [rejectionReason, regenerationWishes]
              .filter(Boolean)
              .join('\n') || baseItem.last_regeneration_feedback || item.last_regeneration_feedback || null

            return {
              last_regeneration_dislike: rejectionReason,
              last_regeneration_wish: regenerationWishes,
              last_regeneration_feedback: combinedFeedback,
            }
          }

          if (!activeCaption) {
            return {
              ...baseItem,
              caption_id: existingRowKey || baseItem.caption_id || null,
              caption_text:
                typeof item.caption_text === 'string'
                  ? item.caption_text
                  : item.caption?.caption_text || '',
              ...mergeFeedback(null),
            }
          }

          return {
            ...baseItem,
            caption_id: existingRowKey || baseItem.caption_id || activeCaption.caption_id,
            caption_uuid: activeCaption.caption_id,
            caption_text: activeCaption.caption_text,
            rendered_sas_url: activeCaption.rendered_sas_url,
            caption_version: activeCaption.version,
            ...mergeFeedback(activeCaption),
          }
        } catch (captionError) {
          console.error('Failed to load caption for image', item.image_id, captionError)
          const fallbackCaptionId = resolveCaptionId(item)

          if (fallbackCaptionId) {
            return {
              ...item,
              caption_id: fallbackCaptionId,
              caption_row_key: fallbackCaptionId,
              caption_text:
                typeof item.caption_text === 'string'
                  ? item.caption_text
                  : item.caption?.caption_text || '',
              last_regeneration_dislike: item.last_regeneration_dislike ?? null,
              last_regeneration_wish: item.last_regeneration_wish ?? null,
              last_regeneration_feedback: item.last_regeneration_feedback ?? null,
            }
          }

          return {
            ...item,
            caption_row_key: item.caption_row_key || null,
            caption_text:
              typeof item.caption_text === 'string'
                ? item.caption_text
                : item.caption?.caption_text || '',
            last_regeneration_dislike: item.last_regeneration_dislike ?? null,
            last_regeneration_wish: item.last_regeneration_wish ?? null,
            last_regeneration_feedback: item.last_regeneration_feedback ?? null,
          }
        }
      })
    )
  }, [selectedAccountId])

  const clearRegenerationWatcher = useCallback((imageId) => {
    const watchers = regenerationWatchers.current
    const timeoutId = watchers.get(imageId)
    if (timeoutId) {
      clearTimeout(timeoutId)
      watchers.delete(imageId)
    }
  }, [])

  const refreshPendingItem = useCallback(
    async (imageId, sentiment, { bannerMessage } = {}) => {
      try {
        if (!imageId || !sentiment || !selectedAccountId) {
          return
        }

        const imageParams = {
          sentiment,
          instagram_account_id: selectedAccountId
        }
        const captionParams = {
          image_id: imageId,
          limit: 10,
          instagram_account_id: selectedAccountId
        }

        const [imageData, captionResponse] = await Promise.all([
          api.getImageById(imageId, imageParams),
          api.getCaptions(captionParams),
        ])

        const captions = captionResponse?.captions ?? []
        const latestCaption = captions
          .slice()
          .sort((a, b) => {
            const aDate = new Date(a.created_at || a.createdAt || 0)
            const bDate = new Date(b.created_at || b.createdAt || 0)
            return bDate - aDate
          })[0]

        setPendingItems((prev) =>
          prev.map((item) => {
            if (item.image_id !== imageId) {
              return item
            }

            const mergedCaption = latestCaption
              ? {
                  ...item.caption,
                  ...latestCaption,
                }
              : item.caption

            const rejectionReason =
              latestCaption?.rejection_reason ??
              imageData?.rejection_reason ??
              item.last_regeneration_dislike ??
              null
            const regenerationWishes =
              latestCaption?.regeneration_wishes ??
              imageData?.regeneration_wishes ??
              item.last_regeneration_wish ??
              null

            const combinedFeedback = [rejectionReason, regenerationWishes]
              .filter(Boolean)
              .join('\n')

            return {
              ...item,
              ...imageData,
              caption: mergedCaption,
              caption_text:
                typeof mergedCaption?.caption_text === 'string'
                  ? mergedCaption.caption_text
                  : mergedCaption?.caption ?? item.caption_text,
              caption_id: mergedCaption?.caption_id || item.caption_id,
              regeneration_in_progress: false,
              last_regeneration_dislike: rejectionReason,
              last_regeneration_wish: regenerationWishes,
              last_regeneration_feedback: combinedFeedback || null,
              requested_text_color_hex: null,
              requested_text_background_color_hex: null,
              requested_text_has_background: null,
            }
          })
        )

        clearRegenerationWatcher(imageId)

        if (bannerMessage) {
          setActionMessage(bannerMessage)
        }
      } catch (refreshError) {
        console.error('Failed to refresh pending item:', refreshError)
      }
    },
    [clearRegenerationWatcher, selectedAccountId]
  )

  const loadProcessingCount = useCallback(
    async (options = {}) => {
      const { silent = false } = options

      try {
        if (!silent) {
          setLoadingProcessing(true)
        }

        const counts = await api.getProcessingCount(selectedAccountId)

        // Ignore response if component unmounted or account changed
        if (!isMounted.current) {
          return
        }

        setProcessingCount(counts)
      } catch (err) {
        if (!isMounted.current) {
          return
        }

        console.error('Failed to load processing count:', err)
        // Non-critical error, just log it
      } finally {
        if (!silent && isMounted.current) {
          setLoadingProcessing(false)
        }
      }
    },
    [selectedAccountId]
  )

  const loadPendingItems = useCallback(
    async (options = {}) => {
      const { silent = false } = options

      try {
        if (!silent) {
          setLoading(true)
        }
        setError(null)

        const params = {}
        if (selectedAccountId) {
          params.instagram_account_id = selectedAccountId
        }

        // Fetch unified approval queue (images + videos)
        const result = await api.getApprovals(params)

        // DEBUG: Log API response
        console.log('[ApprovalPage] API Response:', {
          total: result.items?.length,
          pending_count: result.pending_count,
          processing_count: result.processing_count,
          items: result.items
        })

        // Ignore response if component unmounted or account changed
        if (!isMounted.current || params.instagram_account_id !== selectedAccountId) {
          return
        }

        const allItems = result.items || []

        // Separate images, videos, and product proposals
        const images = allItems.filter((item) => item.type === 'image')
        const videos = allItems.filter((item) => item.type === 'video')
        const products = allItems.filter((item) => item.type === 'product')

        // DEBUG: Log separated counts
        console.log('[ApprovalPage] Separated items:', {
          totalItems: allItems.length,
          images: images.length,
          videos: videos.length,
          products: products.length,
          videoIds: videos.map(v => v.video_id),
          productIds: products.map(p => p.id || p.proposal_id)
        })

        // Enrich only images with captions (videos and products don't need caption enrichment)
        const enrichedImages = await enrichWithCaptions(images)

        // Combine: enriched images + videos + products
        const combined = [...enrichedImages, ...videos, ...products]

        setPendingItems(combined)

        const activeImageIds = new Set(enrichedImages.map((item) => item.image_id))
        regenerationWatchers.current.forEach((timeoutId, imageId) => {
          if (!activeImageIds.has(imageId)) {
            clearTimeout(timeoutId)
            regenerationWatchers.current.delete(imageId)
          }
        })
      } catch (err) {
        console.error('Failed to load pending items:', err)
        setError(err.message || t('page.error.loadFailed'))
      } finally {
        if (!silent) {
          setLoading(false)
        }
      }
    },
    [enrichWithCaptions, selectedAccountId, t]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!isLoadingAccounts && selectedAccountId) {
      loadPendingItems()
      loadProcessingCount()
    }
  }, [selectedAccountId, isLoadingAccounts, loadPendingItems, loadProcessingCount])

  // Validate selectedItem still exists in queue
  useEffect(() => {
    if (!selectedItem) return

    const itemExists = pendingItems.some(item => {
      if (item.type === 'video' && selectedItem.type === 'video') {
        return item.video_id === selectedItem.video_id
      }
      if (item.type === 'image' && selectedItem.type === 'image') {
        return item.image_id === selectedItem.image_id
      }
      if (item.type === 'product' && selectedItem.type === 'product') {
        // Compare by id or proposal_id as fallback
        const itemKey = item.id || item.proposal_id
        const selectedKey = selectedItem.id || selectedItem.proposal_id
        return itemKey && selectedKey && itemKey === selectedKey
      }
      return false
    })

    if (!itemExists) {
      setSelectedItem(null)
    }
  }, [selectedItem, pendingItems])

  const scheduleRegenerationWatcher = useCallback(
    (imageId, sentiment, attempt = 0) => {
      if (!imageId || !sentiment || !selectedAccountId) {
        return
      }

      clearRegenerationWatcher(imageId)

      const FAST_POLL_ATTEMPTS = 24 // ~2 minutes at 5s interval
      const MAX_TOTAL_ATTEMPTS = 120 // Extend polling up to ~45 minutes with slower interval
      const FAST_DELAY_MS = 5000
      const SLOW_DELAY_MS = 30000

      const runCheck = async () => {
        let shouldContinue = true

        try {
          const params = {
            sentiment,
            instagram_account_id: selectedAccountId
          }
          const imageData = await api.getImageById(imageId, params)

          // For caption regeneration: check regeneration_in_progress flag
          // For GIF rendering: check status is not rendering_gif
          const isRegenerationDone = !imageData?.regeneration_in_progress
          const isGifRenderingDone = imageData?.status !== 'rendering_gif'

          // Stop polling when BOTH regeneration and GIF rendering are done
          if (isRegenerationDone && isGifRenderingDone) {
            await refreshPendingItem(imageId, sentiment, {
              bannerMessage: t('page.messages.regenerationCompleted'),
            })
            shouldContinue = false
          }
        } catch (pollError) {
          console.warn('Regeneration/GIF rendering poll failed:', pollError)
        }

        if (!shouldContinue) {
          return
        }

        const nextAttempt = attempt + 1

        if (nextAttempt === FAST_POLL_ATTEMPTS) {
          setActionMessage(t('page.messages.regenerationStillWaiting'))
        }

        if (nextAttempt >= MAX_TOTAL_ATTEMPTS) {
          regenerationWatchers.current.delete(imageId)
          loadPendingItems({ silent: true })
          return
        }

        const delay =
          nextAttempt >= FAST_POLL_ATTEMPTS ? SLOW_DELAY_MS : FAST_DELAY_MS

        const timeoutId = setTimeout(() => {
          scheduleRegenerationWatcher(imageId, sentiment, nextAttempt)
        }, delay)
        regenerationWatchers.current.set(imageId, timeoutId)
      }

      const initialDelay =
        attempt >= FAST_POLL_ATTEMPTS ? SLOW_DELAY_MS : FAST_DELAY_MS
      const timeoutId = setTimeout(runCheck, initialDelay)
      regenerationWatchers.current.set(imageId, timeoutId)
    },
    [clearRegenerationWatcher, loadPendingItems, refreshPendingItem, selectedAccountId, t]
  )

  useEffect(() => {
    const watchersAtMount = regenerationWatchers.current
    return () => {
      watchersAtMount.forEach((timeoutId) => clearTimeout(timeoutId))
      watchersAtMount.clear()
    }
  }, [])

  useEffect(() => {
    if (!isConnected) {
      return undefined
    }

    // Memory leak prevention: track if component is still mounted
    let isMounted = true

    const handleCaptionEvent = async (eventPayload = {}) => {
      if (!isMounted) return

      const imageId =
        eventPayload.image_id || eventPayload.imageId || eventPayload.ImageId
      if (!imageId) {
        return
      }

      const sentiment =
        eventPayload.sentiment || eventPayload.sentiment_category || resolveSentiment(eventPayload)

      // Optimistic flag clear
      if (isMounted) {
        setPendingItems((prev) =>
          prev.map((item) =>
            item.image_id === imageId
              ? { ...item, regeneration_in_progress: false }
              : item
          )
        )
      }

      await refreshPendingItem(imageId, sentiment, {
        bannerMessage: t('page.messages.newCaptionAvailable'),
      })

      // Refresh processing count when caption is generated (image moves from analyzed -> awaiting_approval)
      if (isMounted) {
        loadProcessingCount({ silent: true })
      }
    }

    const handleImageUploadEvent = async () => {
      if (!isMounted) return

      // Refresh processing count when new image is uploaded
      loadProcessingCount({ silent: true })
    }

    const unsubscribers = [
      subscribe?.('CaptionGenerated', handleCaptionEvent),
      subscribe?.('CaptionRegenerated', handleCaptionEvent),
      subscribe?.('ImageUploaded', handleImageUploadEvent),
      subscribe?.('ImageAnalyzed', handleImageUploadEvent),
    ].filter(Boolean)

    return () => {
      isMounted = false
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe())
    }
  }, [clearRegenerationWatcher, isConnected, refreshPendingItem, subscribe, loadProcessingCount, t])

  const handleApprove = async (item, captionText, options = {}) => {
    // Prevent double-clicks by checking if item is already being approved
    if (approvingItems.has(item.image_id)) {
      return
    }

    try {
      setApprovingItems(prev => new Set([...prev, item.image_id]))
      setProcessingAction(true)
      setError(null)

      const captionId = resolveCaptionId(item)
      if (!captionId) {
        throw new Error(t('page.validation.noCaption'))
      }

      const sentiment = resolveSentiment(item)
      if (!sentiment) {
        throw new Error(t('page.validation.noSentiment'))
      }

      const payload = {
        image_id: item.image_id,
        sentiment: sentiment,
        scheduled_time: options.scheduledTime,
        caption_text: captionText,
      }
      if (selectedAccountId) {
        payload.instagram_account_id = selectedAccountId
      }

      const approvalResponse = await api.approveCaption(captionId, payload)

      // Track approval event
      trackStoryApproved()
      if (options.scheduledTime) {
        trackStoryScheduled(1)
      }

      // For GIF flow, update item with rendering_gif status instead of removing
      const isGifFlow = approvalResponse?.status === 'rendering_gif'

      if (isGifFlow) {
        // Update item with rendering_gif status for polling
        // Note: regeneration_in_progress is NOT set for GIF rendering - only for caption regeneration
        setPendingItems((prev) =>
          prev.map((pending) =>
            pending.image_id === item.image_id
              ? {
                  ...pending,
                  status: 'rendering_gif',
                }
              : pending
          )
        )
        // Schedule polling watcher to monitor GIF rendering progress
        scheduleRegenerationWatcher(item.image_id, sentiment)
      } else {
        // Non-GIF flow: remove from pending items
        setPendingItems((prev) => prev.filter((pending) => pending.image_id !== item.image_id))
        clearRegenerationWatcher(item.image_id)
      }

      setSelectedItem(null)
      setActionMessage(t('page.messages.approved'))

      // Refresh pending count badge in navigation
      refreshPendingCount()
    } catch (err) {
      console.error('Failed to approve item:', err)
      setError(err.message || t('page.error.approveFailed'))
    } finally {
      setApprovingItems(prev => {
        const next = new Set(prev)
        next.delete(item.image_id)
        return next
      })
      setProcessingAction(false)
    }
  }

  /**
   * Handle video approval
   * Videos don't have captions or style options - straightforward approval workflow
   */
  const handleApproveVideo = async (videoItem) => {
    try {
      setProcessingAction(true)
      setError(null)

      // Validate video_id exists
      if (!videoItem?.video_id) {
        throw new Error('Invalid video: missing video_id')
      }

      const params = {}
      if (selectedAccountId) {
        params.instagram_account_id = selectedAccountId
      }

      // Approve video via API
      await api.approveVideo(videoItem.video_id, params)

      // Track approval event
      trackStoryApproved()

      // Remove video from pending items
      setPendingItems((prev) => prev.filter((item) => item.video_id !== videoItem.video_id))

      setSelectedItem(null)
      setActionMessage(t('page.messages.videoApproved', { defaultValue: 'Video approved!' }))

      // Refresh pending count badge in navigation
      refreshPendingCount()
    } catch (err) {
      console.error('Failed to approve video:', err)
      setError(err.message || t('page.error.approveVideoFailed', { defaultValue: 'Failed to approve video' }))
    } finally {
      setProcessingAction(false)
    }
  }

  /**
   * Handle video deletion from approval queue
   */
  const handleDeleteVideo = async (videoItem) => {
    try {
      setProcessingAction(true)
      setError(null)

      // Validate video_id exists
      if (!videoItem?.video_id) {
        throw new Error('Invalid video: missing video_id')
      }

      const params = {}
      if (selectedAccountId) {
        params.instagram_account_id = selectedAccountId
      }

      // Delete video via API
      await api.deleteVideo(videoItem.video_id, params)

      // Remove video from pending items
      setPendingItems((prev) => prev.filter((item) => item.video_id !== videoItem.video_id))

      setSelectedItem(null)
      setActionMessage(t('page.messages.videoDeleted', { defaultValue: 'Video deleted' }))

      // Refresh pending count badge in navigation
      refreshPendingCount()
    } catch (err) {
      console.error('Failed to delete video:', err)
      setError(err.message || t('page.error.deleteVideoFailed', { defaultValue: 'Failed to delete video' }))
    } finally {
      setProcessingAction(false)
    }
  }

  /**
   * Handle product proposal approval
   * Triggers video rendering via ACI container (GIF-Flow pattern)
   */
  const handleApproveProduct = async (proposal, payload = {}) => {
    try {
      setProcessingAction(true)
      setError(null)

      const proposalId = proposal.id || proposal.proposal_id
      if (!proposalId) {
        throw new Error('Invalid proposal: missing proposal_id')
      }

      const params = {
        caption_text: payload.caption_text,
        scheduled_for: payload.scheduled_for,
        ...payload
      }
      if (selectedAccountId) {
        params.instagram_account_id = selectedAccountId
      }

      // Approve proposal via API (triggers video rendering)
      const response = await api.approveProductProposal(proposalId, params)

      // Track approval event
      trackStoryApproved()

      // GIF-Flow pattern: Update item to rendering_video status
      setPendingItems((prev) =>
        prev.map((item) =>
          (item.id === proposalId || item.proposal_id === proposalId)
            ? { ...item, status: 'rendering_video', render_task_id: response?.render_task_id }
            : item
        )
      )

      setSelectedItem(null)
      setActionMessage(t('modal.product.toast.approved', { defaultValue: 'Proposal approved! Video is being rendered.' }))

      // Refresh pending count badge in navigation
      refreshPendingCount()
    } catch (err) {
      console.error('Failed to approve product proposal:', err)
      setError(err.message || t('modal.product.toast.approveError', { error: err.message, defaultValue: 'Failed to approve proposal' }))
    } finally {
      setProcessingAction(false)
    }
  }

  /**
   * Handle product proposal rejection
   */
  const handleRejectProduct = async (proposal, reason = '') => {
    try {
      setProcessingAction(true)
      setError(null)

      const proposalId = proposal.id || proposal.proposal_id
      if (!proposalId) {
        throw new Error('Invalid proposal: missing proposal_id')
      }

      const params = { reason }
      if (selectedAccountId) {
        params.instagram_account_id = selectedAccountId
      }

      // Reject proposal via API
      await api.rejectProductProposal(proposalId, params)

      // Remove from pending items
      setPendingItems((prev) => prev.filter((item) =>
        item.id !== proposalId && item.proposal_id !== proposalId
      ))

      setSelectedItem(null)
      setActionMessage(t('modal.product.toast.rejected', { defaultValue: 'Proposal rejected' }))

      // Refresh pending count badge in navigation
      refreshPendingCount()
    } catch (err) {
      console.error('Failed to reject product proposal:', err)
      setError(err.message || t('modal.product.toast.rejectError', { error: err.message, defaultValue: 'Failed to reject proposal' }))
    } finally {
      setProcessingAction(false)
    }
  }

  const handleRegenerate = async (item, feedbackDetails = {}) => {
    try {
      setProcessingAction(true)
      setError(null)

      const colorOnly = Boolean(feedbackDetails.colorOnly)
      let trimmedReason = (feedbackDetails.rejectionReason || '').trim()
      let trimmedWishes = (feedbackDetails.regenerationWishes || '').trim()
      let successMessage = t('page.messages.regenerationRequested')

      const previousCaptionId = resolveCaptionId(item)
      if (!previousCaptionId) {
        throw new Error(t('page.validation.noCaptionRegenerate'))
      }

      const payload = {
        image_id: item.image_id,
        previous_caption_id: previousCaptionId,
      }

      const sentiment = resolveSentiment(item)
      if (sentiment) {
        payload.sentiment = sentiment
      }

      if (selectedAccountId) {
        payload.instagram_account_id = selectedAccountId
      }

      if (colorOnly) {
        payload.color_only = true
        // Include edited caption text if provided
        if (feedbackDetails.caption) {
          payload.caption_text = feedbackDetails.caption
        }
        if (feedbackDetails.textColor) {
          payload.text_color_hex = feedbackDetails.textColor
        }
        if (feedbackDetails.textBackgroundColor) {
          payload.text_background_color_hex = feedbackDetails.textBackgroundColor
        }
        if (typeof feedbackDetails.textHasBackground === 'boolean') {
          payload.text_has_background = feedbackDetails.textHasBackground
        }

        await api.regenerateCaption(payload)
        trackCaptionRegeneration()
        successMessage = t('page.messages.colorRerendering')
      } else {
        if (!trimmedReason) {
          throw new Error(t('page.validation.rejectionRequired'))
        }

        if (!trimmedWishes) {
          throw new Error(t('page.validation.wishesRequired'))
        }

        payload.rejection_reason = trimmedReason
        payload.regeneration_wishes = trimmedWishes

        // ✅ FIX BUG #3: Include caption_text if user edited it
        if (feedbackDetails.caption) {
          payload.caption_text = feedbackDetails.caption
        }

        const response = await api.regenerateCaption(payload)
        trackCaptionRegeneration()
        trimmedReason = response?.rejection_reason || trimmedReason
        trimmedWishes = response?.regeneration_wishes || trimmedWishes
      }

      const combinedFeedback = [trimmedReason, trimmedWishes]
        .filter(Boolean)
        .join('\n') || null

      setPendingItems((prev) =>
        prev.map((pending) =>
          pending.image_id === item.image_id
            ? {
                ...pending,
                regeneration_in_progress: true,
                last_regeneration_dislike: colorOnly
                  ? pending.last_regeneration_dislike ?? null
                  : trimmedReason || null,
                last_regeneration_wish: colorOnly
                  ? pending.last_regeneration_wish ?? null
                  : trimmedWishes || null,
                last_regeneration_feedback: colorOnly
                  ? pending.last_regeneration_feedback ?? null
                  : combinedFeedback,
                requested_text_color_hex:
                  feedbackDetails.textColor || pending.requested_text_color_hex || null,
                requested_text_background_color_hex:
                  feedbackDetails.textBackgroundColor || pending.requested_text_background_color_hex || null,
                requested_text_has_background:
                  typeof feedbackDetails.textHasBackground === 'boolean'
                    ? feedbackDetails.textHasBackground
                    : pending.requested_text_has_background,
              }
            : pending
        )
      )

      setActionMessage(successMessage)
      setSelectedItem(null)

      scheduleRegenerationWatcher(item.image_id, sentiment)
    } catch (err) {
      console.error('Failed to regenerate caption:', err)
      setError(err.message || t('page.error.regenerateFailed'))
    } finally {
      setProcessingAction(false)
    }
  }

  const handleRejectImage = async (item, reason) => {
    try {
      setProcessingAction(true)
      setError(null)

      const trimmedReason = (reason || '').trim()
      if (!trimmedReason) {
        throw new Error(t('page.validation.rejectReasonRequired'))
      }

      const payload = {
        reason: trimmedReason,
      }

      const sentiment = resolveSentiment(item)
      if (sentiment) {
        payload.sentiment = sentiment
      }

      if (selectedAccountId) {
        payload.instagram_account_id = selectedAccountId
      }

      await api.rejectImage(item.image_id, payload)

      setPendingItems((prev) => prev.filter((pending) => pending.image_id !== item.image_id))
      setSelectedItem(null)
      setActionMessage(t('page.messages.imageRejected'))
      clearRegenerationWatcher(item.image_id)

      // Refresh pending count badge in navigation
      refreshPendingCount()
    } catch (err) {
      console.error('Failed to reject image:', err)
      setError(err.message || t('page.error.rejectFailed'))
    } finally {
      setProcessingAction(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('page.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('page.header.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('page.header.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Pending Approval Badge */}
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('page.header.pendingCount', { count: pendingItems.length })}
            </span>

            {/* Processing Badge */}
            {processingCount.total > 0 && (
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                title={t('page.header.processingLabel', {
                  pending: processingCount.pending,
                  analyzed: processingCount.analyzed
                })}
                aria-label={t('page.header.processingLabel', {
                  pending: processingCount.pending,
                  analyzed: processingCount.analyzed
                })}
                aria-live="polite"
              >
                <svg className="w-4 h-4 mr-1.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {processingCount.total === 1
                  ? t('page.header.processingCount', { count: processingCount.total })
                  : t('page.header.processingCount_plural', { count: processingCount.total })
                }
              </span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start justify-between space-x-4">
          <div>
            <p className="font-medium">{error}</p>
            <p className="text-xs text-red-600 mt-1">
              {t('page.error.helpText')}
            </p>
          </div>
          <button
            onClick={() => loadPendingItems()}
            className="text-sm font-medium text-red-600 hover:text-red-800"
          >
            {t('page.error.retry')}
          </button>
        </div>
      )}

      {actionMessage && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg flex items-start justify-between space-x-4">
          <p className="text-sm font-medium">{actionMessage}</p>
          <button
            onClick={() => setActionMessage(null)}
            className="text-xs font-semibold uppercase tracking-wide text-blue-700 hover:text-blue-900"
          >
            {t('page.actionMessage.dismiss')}
          </button>
        </div>
      )}

      {/* Images Grid */}
      {pendingItems.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {pendingItems.map((item) => {
            // Render VideoApprovalCard for videos
            if (item.type === 'video') {
              return (
                <VideoApprovalCard
                  key={item.video_id}
                  video={item}
                  onSelect={setSelectedItem}
                />
              )
            }

            // Render ProductProposalCard for product proposals
            if (item.type === 'product') {
              return (
                <ProductProposalCard
                  key={item.id || item.proposal_id}
                  proposal={item}
                  onSelect={setSelectedItem}
                />
              )
            }

            // Render ImageApprovalCard for images
            const rawCaption =
              item.caption?.caption_text ?? item.caption_text ?? item.caption ?? ''

            const captionText = typeof rawCaption === 'string' ? rawCaption : ''

            const imageSrc =
              item.caption?.rendered_sas_url ||
              item.caption?.sas_url ||
              item.rendered_sas_url ||
              item.sas_url ||
              item.preview_url ||
              item.blob_url ||
              buildBlobUrl(item.rendered_blob_path) ||
              buildBlobUrl(item.blob_path) ||
              FALLBACK_IMAGE

            return (
            <div
              key={item.image_id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <div className="aspect-story bg-gray-100 relative">
                <img
                  src={imageSrc}
                  alt={captionText || t('page.grid.imageAlt')}
                  className="w-full h-full object-cover"
                  onError={applyFallbackIfNeeded}
                />
                {item.status === 'rendering_gif' && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 shadow animate-pulse">
                      <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-purple-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('page.grid.renderingGifBadge', 'Rendering GIF...')}
                    </span>
                  </div>
                )}
                {/* Only show regenerating badge for caption regeneration, NOT for GIF rendering */}
                {item.regeneration_in_progress && item.status !== 'rendering_gif' && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 shadow">
                      {t('page.grid.regeneratingBadge')}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-4">
                {captionText && (
                  <p className="text-sm text-gray-700 line-clamp-2 mb-3">
                    {captionText}
                  </p>
                )}

                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedItem(item)}
                    disabled={approvingItems.has(item.image_id) || item.regeneration_in_progress || item.status === 'rendering_gif'}
                    className={`flex-1 btn-primary py-2 text-sm ${
                      approvingItems.has(item.image_id) || item.regeneration_in_progress || item.status === 'rendering_gif'
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                  >
                    {t('page.grid.reviewButton')}
                  </button>
                  <button
                    onClick={() => handleApprove(item, captionText)}
                    disabled={approvingItems.has(item.image_id) || item.regeneration_in_progress || item.status === 'rendering_gif'}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      approvingItems.has(item.image_id) || item.regeneration_in_progress || item.status === 'rendering_gif'
                        ? 'bg-green-600 text-white opacity-50 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                    title={
                      approvingItems.has(item.image_id)
                        ? t('page.grid.approving', { defaultValue: 'Approving...' })
                        : item.status === 'rendering_gif'
                          ? t('page.grid.waitingForGifRender', { defaultValue: 'Waiting for GIF to render...' })
                          : t('page.grid.quickApprove')
                    }
                  >
                    {approvingItems.has(item.image_id) ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Show regeneration message only for caption regeneration, not GIF rendering */}
                {item.regeneration_in_progress && item.status !== 'rendering_gif' && (
                  <p className="mt-3 text-xs text-yellow-700">
                    {t('page.grid.regenerationInProgress')}
                  </p>
                )}
                {/* Show GIF rendering message when rendering GIF */}
                {item.status === 'rendering_gif' && (
                  <p className="mt-3 text-xs text-blue-700">
                    ✨ {t('page.grid.gifRenderingInProgress', { defaultValue: 'Rendering GIF with sticker...' })}
                  </p>
                )}
              </div>
            </div>
            )
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('page.empty.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('page.empty.subtitle')}
            </p>
            <button
              onClick={() => loadPendingItems()}
              className="mt-4 btn-primary"
            >
              {t('page.empty.refresh')}
            </button>
          </div>
        </div>
      )}

      {/* Approval Modal - V3 (modular) or V2 (legacy) based on feature flag */}
      {selectedItem && (
        <ErrorBoundary
          errorTitle="Modal Error"
          errorMessage="An error occurred while loading the approval modal. Please try again or refresh the page."
          onReset={() => setSelectedItem(null)}
        >
          {USE_APPROVAL_MODAL_V3 ? (
            <ApprovalModalV3
              item={selectedItem}
              isOpen={!!selectedItem}
              onClose={() => setSelectedItem(null)}
              onApprove={(itemWithResponse) => {
                // NOTE: Modal already sends the API request itself!
                // This callback is only for UI state updates
                const isGifFlow = itemWithResponse?.status === 'rendering_gif'

                if (isGifFlow) {
                  setPendingItems((prev) =>
                    prev.map((pending) =>
                      pending.image_id === itemWithResponse.image_id
                        ? { ...pending, status: 'rendering_gif' }
                        : pending
                    )
                  )
                  const sentiment = resolveSentiment(itemWithResponse)
                  scheduleRegenerationWatcher(itemWithResponse.image_id, sentiment)
                } else {
                  setPendingItems((prev) => prev.filter((pending) => pending.image_id !== itemWithResponse.image_id))
                  clearRegenerationWatcher(itemWithResponse.image_id)
                }

                setSelectedItem(null)
                setActionMessage(t('page.messages.approved'))
                refreshPendingCount()
                trackStoryApproved()
              }}
              onApproveVideo={handleApproveVideo}
              onDeleteVideo={handleDeleteVideo}
              onApproveProduct={handleApproveProduct}
              onRejectProduct={handleRejectProduct}
              onUpdate={() => loadPendingItems({ silent: true })}
              onRejectImage={handleRejectImage}
            />
          ) : (
            <ApprovalModalV2
              item={selectedItem}
              isOpen={!!selectedItem}
              onClose={() => setSelectedItem(null)}
              onApprove={(itemWithResponse) => {
                // NOTE: ApprovalModalV2 already sends the API request itself!
                // This callback is only for UI state updates - DO NOT call handleApprove
                // which would send a duplicate API request.
                const isGifFlow = itemWithResponse?.status === 'rendering_gif'

                if (isGifFlow) {
                  // GIF flow: update item status for polling instead of removing
                  setPendingItems((prev) =>
                    prev.map((pending) =>
                      pending.image_id === itemWithResponse.image_id
                        ? { ...pending, status: 'rendering_gif' }
                        : pending
                    )
                  )
                  // Schedule polling watcher to monitor GIF rendering progress
                  const sentiment = resolveSentiment(itemWithResponse)
                  scheduleRegenerationWatcher(itemWithResponse.image_id, sentiment)
                } else {
                  // Non-GIF flow: remove from pending items
                  setPendingItems((prev) => prev.filter((pending) => pending.image_id !== itemWithResponse.image_id))
                  clearRegenerationWatcher(itemWithResponse.image_id)
                }

                setSelectedItem(null)
                setActionMessage(t('page.messages.approved'))
                refreshPendingCount()
                trackStoryApproved()
              }}
              onApproveVideo={handleApproveVideo}
              onDeleteVideo={handleDeleteVideo}
              onApproveProduct={handleApproveProduct}
              onRejectProduct={handleRejectProduct}
              onUpdate={() => {
                // Refresh the pending items list after regeneration
                loadPendingItems({ silent: true })
              }}
              onRejectImage={handleRejectImage}
            />
          )}
        </ErrorBoundary>
      )}
    </div>
  )
}

export default ApprovalPage
